/**
 * /settings/profile/billing/change — confirm an in-app plan change. Plans
 * and Pricing links here with the change and
 * the screen to return to; the page itself is `PlanChangeConfirm`.
 */

import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { PlanChangeConfirm } from "~/components/nova/plan-change-confirm";
import { ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { BILLING_TERMS } from "~/lib/shared/plan-catalogue";
import { PLAN_CHANGE_MODES } from "~/lib/shared/plan-change";
import { parseReturnPath } from "~/lib/shared/return-path";

export const Route = createFileRoute("/_user/settings/profile/billing_/change")(
  {
    staticData: { title: "Change plan" },
    validateSearch: z.object({
      mode: z.enum(PLAN_CHANGE_MODES).optional().catch(undefined),
      term: z.enum(BILLING_TERMS).optional().catch(undefined),
      returnTo: z
        .unknown()
        .transform((value) => parseReturnPath(value) ?? undefined),
    }),
    head: () =>
      createNoIndexMeta(
        "Change plan – Dream Coach Match",
        "Confirm a change to your NOVA plan",
      ),
    component: ChangePlanPage,
  },
);

function ChangePlanPage() {
  const { mode, term, returnTo } = Route.useSearch();
  if (!mode || !term) {
    return (
      <Card>
        <p role="alert" className="text-text-secondary text-sm">
          This link doesn’t name a plan change. Choose one on Plans and Pricing.
        </p>
        <ButtonRouter
          search={{ returnTo }}
          to="/settings/profile/billing"
          variant="primary"
        >
          Go to Plans and Pricing
        </ButtonRouter>
      </Card>
    );
  }
  return <PlanChangeConfirm mode={mode} term={term} returnTo={returnTo} />;
}
